import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { COURSE_DISCIPLINES, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { AtpService } from 'app/services/atp/atp.service';
import { CommonDataService } from 'app/services/common/common-data.service';
import { SharedService } from 'app/services/core/shared.service';
import { ToastrService } from 'ngx-toastr';
import { IAtpStudentFilter } from 'app/models/filters/atpStudentFilter';
import { AddEditAtpCourseComponent } from '../add-edit-atp/add-edit-atp-course/add-edit-atp-course.component';
import { AddEditCourseStudentComponent } from '../add-edit-atp/add-edit-course-student/add-edit-course-student.component';
import { AddEditAtpComponent } from '../add-edit-atp/add-edit-atp.component';
import { AddEditIndividualComponent } from 'app/components/individual/add-edit-individual/add-edit-individual.component';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IPerson } from 'app/models/People/person';
import { MergeIndividualComponent } from 'app/components/individual/merge-individual/merge-individual.component';
import * as XLSX from 'xlsx';
import { IAtpCourseStudent } from 'app/models/atp/atpCourseStudent';

@Component({
  selector: 'app-atp-student',
  templateUrl: './atp-student.component.html',
  styleUrls: ['./atp-student.component.scss']
})
export class AtpStudentComponent {
  public isMobile: boolean = false;
  public uiData = LEAD_BASED_PAINT;
  public loading: boolean = true;
  public pageSize: number = 50;  
  public students: any[] = [];
  public dataSource: MatTableDataSource<any>;
  public subTableDataSource: MatTableDataSource<any>;
  public filteredStudents: any[] = [];  
  public subTable: any[] = [];  
  public atpList: any[] = [];
  public filteredAtpList: any[] = [];  
  public selectedStudents: IPerson[] = [];

  public filterString: string = '';
  public filterATPString: any = '';
  public filterCourseString: string = '';
  public pendingOnly: boolean = false;
  public selectedDisciplines = new FormControl([]);    
  public startDate = new Date(new Date().setMonth(new Date().getMonth() - 6));

  public endDate = new Date();
  public studentFilter: IAtpStudentFilter = {
    filterString: '', 
    filterATPString: '', 
    filterCourseString: '',
    selectedDisciplines: '', 
    startDate: this.startDate.toISOString(),
    endDate: this.endDate.toISOString()
  };

  @ViewChild('TablePaginator') paginator: MatPaginator;
  @ViewChild('TableSort') sort: MatSort;
  @ViewChild('NestedTableSort') subsort: MatSort;
  @ViewChild('NestedTablePaginator') subpaginator: MatPaginator;
  
  studentDataFields = {    
    layout:{
      columns:['select', 'id', 'firstName', 'lastName', 'atpCourses'],
      container:[
        {displayName:'',columnName:'select', type:'select', size:'5'},
        {displayName:'Person Id',columnName:'id', type:'string', size:'10'},        
        {displayName:'First Name',columnName:'firstName', type:'string', size:'10'},        
        {displayName:'Last Name',columnName:'lastName', type:'string', size:'15'}, 
        {displayName:'Student Records',columnName:'atpCourses', type:'subtable', size:'60'},                
      ],        
    }
  }

  subTableFields = {    
    layout:{      
      columns:['name', 'courseId', 'accreditationDiscipline', 'courseCertificateNumber', 'testScore', 'courseStart','actions'],      
      container:[        
        {displayName:'ATP',columnName:'name', type:'atp', size:'25'},  
        {displayName:'Course Id',columnName:'courseId', type:'course', size:'10'},  
        {displayName:'Discipline',columnName:'accreditationDiscipline', type:'course', size:'13'},  
        {displayName:'Certificate',columnName:'courseCertificateNumber', type:'string', size:'18'},     
        {displayName:'Test Score',columnName:'testScore', type:'string', size:'12'},           
        {displayName:'Course Date',columnName:'courseStart', type:'course', size:'13'},          
      ],        
    }
  }
  private readonly debounceString: Subject<string> = new Subject<string>();

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    public sharedService: SharedService,
    public dialog: MatDialog,
    public commonService: CommonDataService,
    public atpService: AtpService,    
    public toastr: ToastrService) 
  {
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe(result => {
      this.isMobile = result.matches;
    });
    this.initializeDebounceFunction();
    this.loadFilters();
    this.loadIndividuals();
  }

  exportToExcel() {
    const exportData = [];
      
    this.filteredStudents.forEach(student => {                 
      student.atpStudentCourses.forEach(course => {
        const row = {
          'Person Id': student.person.id,
          'First Name': student.person.firstName,
          'Last Name': student.person.lastName,
          'ATP': course.atpCourse.atp.name,
          'Course Id': course.atpCourse.id,
          'Discipline': course.atpCourse.accreditationDiscipline,
          'Certificate': course.courseCertificateNumber,
          'Test Score': course.testScore,
          'Course Date': course.atpCourse.courseStart,
        };          
        exportData.push(row);
      });
    });
      
    exportData.sort((a, b) => a['Last Name'].localeCompare(b['Last Name']));
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      
    ws['!cols'] = [
      { wpx: 75 },
      { wpx: 150 },
      { wpx: 150 },
      { wpx: 250 },
      { wpx: 75 },
      { wpx: 100 },
      { wpx: 150 },
      { wpx: 75 },
      { wpx: 150 },
    ];

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.filterString !== '' ? this.filterString.replace(/\s+/g, '') + '.xlsx' : 'exportedStudentRecords.xlsx');
  }

  loadFilters(): void {
    this.filterString = this.atpService.atpStudentFilterString;
    this.filterATPString = this.atpService.atpStudentFilterATPString;
    this.filterCourseString = this.atpService.atpStudentFilterCourseString;  
    this.pendingOnly = this.atpService.atpStudentPendingOnly;
    this.selectedDisciplines = this.atpService.atpStudentSelectedDisciplines;    
    this.startDate = this.atpService.atpStudentStartDate;
    this.endDate = this.atpService.atpStudentEndDate;

    this.studentFilter.filterString = this.filterString;
    this.studentFilter.filterATPString = this.filterATPString;
    this.studentFilter.filterCourseString = this.filterCourseString;
    this.studentFilter.selectedDisciplines = this.selectedDisciplines.value.join(',');
    this.studentFilter.startDate = this.startDate?.toISOString();
    this.studentFilter.endDate = this.endDate?.toISOString();
  }

  loadIndividuals(): void {  
    this.refreshStudents();
    this.atpService.getATPs().subscribe(result=>{
      this.atpList = result.map(atp=>{return {name: atp.name, id: atp.id}});
      this.atpList.sort((a, b) => a.name.localeCompare(b.name));
      this.filteredAtpList = this.atpList;      
    },error=>{this.toastr.error("Error loading ATPs");})
  }
  
  refreshStudents(){
    this.loading = true;

    if (this.pendingOnly) {
      this.atpService.getAllATPCoursePendingStudents().subscribe(result => {
        const filteredPending = this.applyPendingFilters(result || []);        
        const grouped = this.groupCourseStudentsByPerson(filteredPending);        
        this.students = this.mapStudentsForTable(grouped);
        this.filteredStudents = this.students;
        this.updateDataSource();
        this.loading = false;
      }, error => {
        this.toastr.error("Error loading pending students");
        this.loading = false;
      });
      return;
    }

    this.atpService.getAllStudents(this.studentFilter).subscribe(result=>{            
      this.students = this.mapStudentsForTable(result || []);
      this.filteredStudents = this.students;
      this.updateDataSource();
      this.loading = false;
    },error=>{
      this.toastr.error("Error loading students");
      this.loading = false;
    })    
  }

  private isPendingContext(rowOrNestedRow?: any): boolean {
    if (this.pendingOnly) {
      return true;
    }
    return rowOrNestedRow?.person?.id == null;
  }

  private showPendingInvalidAction(actionLabel: string): void {
    this.toastr.warning(`${actionLabel} is not available for pending students.`);
  }

  private mapStudentsForTable(items: any[]): any[] {
    return (items || []).map(item => {
      const student = {
        ...item,
        id: item.person?.id,
        firstName: item.person?.firstName,
        lastName: item.person?.lastName,
      };

      if (student.atpStudentCourses) {
        const mappedCourses = student.atpStudentCourses.map(course => {
          const courseInfo = course?.atpCourse ?? course?.course;
          return {
            ...course,
            name: courseInfo?.atp?.name,
            courseId: courseInfo?.id,
            accreditationDiscipline: courseInfo?.accreditationDiscipline,
            courseStart: courseInfo?.courseStart,
          };
        });
        student.courses = new MatTableDataSource(mappedCourses);
        student.courses.paginator = this.subpaginator;
        student.courses.sort = this.subsort;
      }

      return student;
    });
  }

  private groupCourseStudentsByPerson(courseStudents: IAtpCourseStudent[]): any[] {
    const groupedByPersonId = new Map<number, any>();
    for (const courseStudent of courseStudents || []) {
      const person = this.getCourseStudentPerson(courseStudent);

      const hasRealPersonId = !!person?.id;
      const pendingNameKey = `${(courseStudent as any)?.lastName ?? ''}|${(courseStudent as any)?.firstName ?? ''}|${(courseStudent as any)?.dateOfBirth ?? ''}`.toLowerCase();
      const personId = hasRealPersonId
        ? person.id
        : ((courseStudent as any)?.studentId ?? (courseStudent as any)?.personId ?? (pendingNameKey.length > 2 ? pendingNameKey : null));
      if (!personId) {
        continue;
      }

      const existing = groupedByPersonId.get(personId);
      if (existing) {
        existing.atpStudentCourses.push(courseStudent);
      } else {
        groupedByPersonId.set(personId, {
          person: person ?? {
            id: null,
            firstName: (courseStudent as any)?.firstName,
            lastName: (courseStudent as any)?.lastName,
          },
          atpStudentCourses: [courseStudent],
        });
      }
    }

    return Array.from(groupedByPersonId.values());
  }

  private getCourseStudentPerson(courseStudent: any): IPerson | null {
    return (
      courseStudent?.student ??
      courseStudent?.person ??
      null
    );
  }

  private getCourseStudentCourse(courseStudent: any): any {
    return (
      courseStudent?.atpCourse ??
      courseStudent?.course ??
      null
    );
  }

  private applyPendingFilters(courseStudents: IAtpCourseStudent[]): IAtpCourseStudent[] {
    const textFilter = (this.studentFilter.filterString || '').toString().trim().toLowerCase();
    const atpFilter = (this.studentFilter.filterATPString || '').toString().trim().toLowerCase();
    const courseIdFilter = (this.studentFilter.filterCourseString || '').toString().trim().toLowerCase();
    const disciplineFilter = (this.studentFilter.selectedDisciplines || '').toString().trim();
    const selectedDisciplines = disciplineFilter.length > 0 ? disciplineFilter.split(',').map(x => x.trim()).filter(Boolean) : [];

    const start = this.studentFilter.startDate ? new Date(this.studentFilter.startDate) : null;
    const end = this.studentFilter.endDate ? new Date(this.studentFilter.endDate) : null;
    if (start && !isNaN(start.getTime())) {
      start.setHours(0, 0, 0, 0);
    }
    if (end && !isNaN(end.getTime())) {
      end.setHours(23, 59, 59, 999);
    }

    return (courseStudents || []).filter(cs => {
      const person = this.getCourseStudentPerson(cs);
      const course = this.getCourseStudentCourse(cs);

      if (courseIdFilter.length > 0) {
        const courseId = course?.id?.toString().toLowerCase() || '';
        if (!courseId.includes(courseIdFilter)) {
          return false;
        }
      }

      if (selectedDisciplines.length > 0) {
        const discipline = course?.accreditationDiscipline || '';
        if (!selectedDisciplines.includes(discipline)) {
          return false;
        }
      }

      if (start || end) {
        if (course?.courseStart) {
          const courseStart = new Date(course.courseStart as any);
          if (!isNaN(courseStart.getTime())) {
            if (start && courseStart < start) {
              return false;
            }
            if (end && courseStart > end) {
              return false;
            }
          }
        }
      }

      if (atpFilter.length > 0) {
        const atpName = (course?.atp?.name || '').toString().toLowerCase();
        const atpId = (course?.atp?.id ?? '').toString().toLowerCase();
        if (!(atpName.includes(atpFilter) || atpId.includes(atpFilter))) {
          return false;
        }
      }

      if (textFilter.length > 0) {
        const personId = (person?.id ?? '').toString().toLowerCase();
        const firstName = ((person?.firstName ?? (cs as any)?.firstName) || '').toString().toLowerCase();
        const lastName = ((person?.lastName ?? (cs as any)?.lastName) || '').toString().toLowerCase();
        const certificate = (cs?.courseCertificateNumber || '').toString().toLowerCase();
        const courseId = (course?.id ?? '').toString().toLowerCase();

        if (!(
          personId.includes(textFilter) ||
          firstName.includes(textFilter) ||
          lastName.includes(textFilter) ||
          certificate.includes(textFilter) ||
          courseId.includes(textFilter)
        )) {
          return false;
        }
      }

      return true;
    });
  }
  
  updateDataSource(){
    this.dataSource = new MatTableDataSource<any>(this.filteredStudents);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  editATP(row: any){
    if (this.isPendingContext(row)) {
      this.showPendingInvalidAction('Edit ATP');
      return;
    }
    const dialogRef = this.dialog.open(AddEditAtpComponent, {
      width: '90%',
      data: {atpId: row.atpCourse.atp.id},});
    dialogRef.afterClosed().subscribe(result => {     
    });
  }

  editPerson(row: any) {       
    if (this.isPendingContext(row)) {
      this.showPendingInvalidAction('Edit person');
      return;
    }
    const dialogRef = this.dialog.open(AddEditIndividualComponent, {
      width: '90%',
      data: {person: row},});
    dialogRef.afterClosed().subscribe(result => {      
    });
  }
  
  editStudent(row: any, nestedRow: any){    
    if (this.isPendingContext(row)) {
      this.showPendingInvalidAction('Edit student record');
      return;
    }
    nestedRow.student = row.person;
    const dialogRef = this.dialog.open(AddEditCourseStudentComponent, {
      width: '90%',
      autoFocus: false,
      data: {studentId: nestedRow.id},});
    dialogRef.afterClosed().subscribe(result => {   
    });
  }

  editCourse(row: any){
    const dialogRef = this.dialog.open(AddEditAtpCourseComponent, {
      width: '90%',
      data: {courseId: row.atpCourse.id},});
    dialogRef.afterClosed().subscribe(result => {  
    });
  }

  filterTable() {     
    this.saveFilters();
    this.refreshStudents();
  }

  saveFilters(){
    this.atpService.atpStudentFilterString = this.filterString;
    this.atpService.atpStudentFilterATPString = this.filterATPString?.name ?? this.filterATPString;
    this.atpService.atpStudentFilterCourseString = this.filterCourseString;
    this.atpService.atpStudentPendingOnly = this.pendingOnly;
    this.atpService.atpStudentSelectedDisciplines = this.selectedDisciplines;
    this.atpService.atpStudentStartDate = this.startDate;    
    this.atpService.atpStudentEndDate = this.endDate;

    this.studentFilter.filterString = this.filterString;
    this.studentFilter.filterATPString = this.filterATPString?.name ?? this.filterATPString;
    this.studentFilter.filterCourseString = this.filterCourseString;
    this.studentFilter.selectedDisciplines = this.selectedDisciplines.value.join(',');
    this.studentFilter.startDate = this.startDate?.toISOString();
    this.studentFilter.endDate = this.endDate?.toISOString();
  }

  clearFilters(){
    this.filterString = '';
    this.filterATPString = '';
    this.filterCourseString = '';
    this.pendingOnly = false;
    this.selectedDisciplines.setValue([]);
    this.startDate = new Date(new Date().setMonth(new Date().getMonth() - 6));
    this.endDate = new Date();
    
    this.selectedStudents = [];
    this.filterTable();
  }

  displayLimit(text: any, limit: string){
    let newText = text;
    if (typeof text === 'string' && text.length > parseInt(limit)){
      newText = newText.substring(0, limit) + "...";
    }    
    return newText;
  }

  getStudentDisciplineValues() {
    return Object.values(COURSE_DISCIPLINES);
  }

  initializeDebounceFunction(): void {
    this.debounceString.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {           
      this.filterATP(value.trim().toLowerCase());     
    });
  }

  filterATP(value: any) {
    if (value.length < 1) {
      this.resetATPList();      
    }
    else{      
      this.filteredAtpList = this.atpList.filter(atp => atp.name.toLowerCase().includes(value) || atp.id.toString().includes(value));
    }
  }

  resetATPList(): void {    
    this.filteredAtpList = this.atpList
  }

  displayATP(atp: any): string {
    if (atp) {
        if (atp.id === 0) {
            return atp?.name ?? atp;
        } else {
            return `${atp?.id ? atp.id + ': ' + atp.name : atp}`;
        }
    } else {
        return '';
    }
  }

  updateDebounceString(event: any){        
    this.debounceString.next(event.target.value);     
  }

  updateATPFilter(): void{
    this.studentFilter.filterATPString = this.filterATPString?.name ?? this.filterATPString;
  }

  mergeStudents(){
    const dialogRef = this.dialog.open(MergeIndividualComponent, {
      width: '90%',
      autoFocus: false,
      data: this.selectedStudents,});
    dialogRef.afterClosed().subscribe(result => {  
      if(result){
        this.selectedStudents = [];
        this.filterTable();
      }
    });
  }

  getMergeTooltip(): string {
    if (this.selectedStudents.length > 0) {
        return 'Merge: ' + this.selectedStudents.map(individual => `${individual.id}: ${individual.firstName} ${individual.lastName}`).join(', ');
    }
    return 'Merges selected individuals';
  }

  updateSelected(studentRecord: any){    
    const index = this.selectedStudents.findIndex(individual => individual.id === studentRecord.person?.id);
    if (index !== -1){
      this.selectedStudents.splice(index, 1);
    } else {            
      let person = studentRecord.person;
      person.affiliations = studentRecord.atpStudentCourses.map(x=>'course:'.concat(x.atpCourse?.id.toString()));
      this.selectedStudents.push(person);      
    }
  }    
  
  isSelected(studentRecord: any): boolean {    
    return this.selectedStudents.some(individual => individual.id === studentRecord.person?.id);
  }

  handleStudentRowClick(row: any, event: any){         
    if(!event.target.className.includes("header")){
      if (this.isPendingContext(row)) {
        this.showPendingInvalidAction('Edit person');
        return;
      }
      this.editPerson(row);
    }
  }
}
